import React from 'react'
import { ChartThemeManager, type ChartTheme } from '../Services/ChartThemeManager'

export interface EmptyChartPlaceholderProps {
  /** Visual theme for the placeholder */
  theme?: ChartTheme
  
  /** Custom title text (defaults to "Empty Chart") */
  title?: string
  
  /** Custom description text (defaults to "Drag curves here to add data") */
  description?: string
  
  /** Custom icon component */
  icon?: React.ReactNode
  
  /** Additional CSS styles */
  style?: React.CSSProperties
  
  /** Custom CSS class name */
  className?: string
  
  /** Whether to show the drag hint text */
  showDragHint?: boolean
}

/**
 * Placeholder component shown when a chart has no data
 * Provides clear visual feedback and instructions for adding data
 */
export function EmptyChartPlaceholder({
  theme = 'dark',
  title = 'Empty Chart',
  description = 'Drag curves here to add data',
  icon,
  style,
  className,
  showDragHint = true
}: EmptyChartPlaceholderProps) {
  const baseStyles = ChartThemeManager.createEmptyChartStyles(theme)
  const combinedStyles = { ...baseStyles, ...style }

  const defaultIcon = (
    <svg
      width="48"
      height="48"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ opacity: 0.4 }}
    >
      {/* Clean wave/signal icon */}
      <path d="M2 12c1-4 3-6 5-6s4 2 5 6c1 4 3 6 5 6s4-2 5-6" />
      <circle cx="12" cy="12" r="1" fill="currentColor" />
    </svg>
  )

  return (
    <div
      className={className}
      style={combinedStyles}
      data-testid="empty-chart-placeholder"
    >
      {/* Icon */}
      <div style={{ marginBottom: '12px' }}>
        {icon || defaultIcon}
      </div>
      
      {/* Title */}
      <div style={{ 
        fontWeight: '500', 
        marginBottom: '8px',
        fontSize: '16px'
      }}>
        {title}
      </div>
      
      {/* Description */}
      {showDragHint && (
        <div style={{ 
          fontSize: '12px',
          opacity: 0.8
        }}>
          {description}
        </div>
      )}
    </div>
  )
}

export default EmptyChartPlaceholder 